import React, { useLayoutEffect } from 'react';
import {
    ActionSegment,
    BaseOptions,
    CommentOptions,
    DetailedError,
    ErrorTypes,
    PhotoOptions,
    Question,
    QuestionAttributeOptions,
    QuestionCategory,
    QuestionType,
    RatingOptions,
    Section,
    SurveyDetails,
    SurveyName,
    SurveySources,
    SurveyType,
} from '@reviews/interfaces';
import * as _ from 'lodash';
import { Comment } from '../Comment/Comment';
import { Rating } from '../RatingScale/Rating';
import { PhotoUpload, PriorPhoto } from '../PhotoUpload/PhotoUpload';
import * as Yup from 'yup';
import { SurveyInfo, SurveyWizard, SurveyWizardStep } from './SurveyWizard';
import './Survey.scss';
import '../../assests/styles/shared.scss';
import { MessagePanel } from '../MessagePanel/MessagePanel';
import { useGetSurveyForReservationQuery } from '../../services';
import { Spinner } from '../../assests/icons';

interface SurveyProps {
    type: SurveyType;
    reservationId: number;
    confirmationCode: string;
    nsuHash: string;
    surveyName: SurveyName;
    commentRequired: boolean;
    isPal?: boolean;
    withContent?: boolean;
}

export const Survey: React.FC<SurveyProps> = (props: SurveyProps) => {
    const { type, reservationId, confirmationCode, nsuHash, surveyName, commentRequired, isPal, withContent } = props;
    const { isLoading, data, error } = useGetSurveyForReservationQuery({
        type,
        reservationId,
        confirmationCode,
        nsuHash,
        name: surveyName,
        isPal,
        withContent,
    });

    const renderQuestion = (
        questionType: QuestionType,
        question: Question,
        context: {
            priorContent?: PriorPhoto[];
            priorTip?: string | null;
            adminReservationId: number;
            nsu: string;
            confirmation: string;
        }
    ): JSX.Element => {
        const { description, name, question_id, title, configuration, required } = question;
        const isPublicReviewQuestion = _.isEqual(name, QuestionCategory.PUBLIC_REVIEW) && _.isEqual(questionType, QuestionType.COMMENT);
        const questionOptions = (configuration as any)?.options || {};
        const referencesPrevious = Boolean(questionOptions.references_previous_content);
        const isContentSurvey = type === SurveyType.CONTENT;
        // Only tip-like COMMENT fields get prior tip; eat/activity questions must stay empty.
        const tipInitialValue =
            referencesPrevious || (isContentSurvey && questionType === QuestionType.COMMENT && !questionOptions.group_heading)
                ? context.priorTip || undefined
                : undefined;
        const photoPriorContent = referencesPrevious ? context.priorContent || [] : [];
        const photoInitialPhotos = isContentSurvey && !referencesPrevious ? context.priorContent || [] : [];

        switch (questionType) {
            case QuestionType.COMMENT:
                return (
                    <Comment
                        name={`${question_id}-${name}-${questionType}`}
                        title={title}
                        description={description}
                        required={isPublicReviewQuestion ? commentRequired : required}
                        configuration={configuration as BaseOptions & CommentOptions}
                        initialValue={tipInitialValue}
                    />
                );
            case QuestionType.RATING:
                return (
                    <Rating
                        name={`${question_id}-${name}-${questionType}`}
                        title={title}
                        description={description}
                        required={required}
                        configuration={configuration as BaseOptions & RatingOptions}
                    />
                );
            case QuestionType.PHOTO:
                return (
                    <PhotoUpload
                        name={`${question_id}-${name}-${questionType}`}
                        title={title}
                        description={description}
                        required={required}
                        configuration={configuration as BaseOptions & PhotoOptions}
                        adminReservationId={context.adminReservationId}
                        nsu={context.nsu}
                        confirmation={context.confirmation}
                        priorContent={photoPriorContent}
                        initialPhotos={photoInitialPhotos}
                    />
                );
            default:
                return <></>;
        }
    };

    const renderQuestionsContainer = (
        questions: Array<Question>,
        context: {
            priorContent?: PriorPhoto[];
            priorTip?: string | null;
            adminReservationId: number;
            nsu: string;
            confirmation: string;
        }
    ): JSX.Element => {
        return (
            <React.Fragment>
                {questions.map((question, index) => (
                    <div key={index} id={`question-${index}`}>
                        {renderQuestion(question.type, question, context)}
                    </div>
                ))}
            </React.Fragment>
        );
    };

    const getGeneralInformation = ({ survey_id, context, name }: SurveyDetails): SurveyInfo => ({
        unit: {
            name: context.unit.name,
            city: context.unit.city || '',
            state: context.unit.state || '',
            img: context.unit.img,
        },
        general_information: {
            survey_id: +survey_id,
            first_name: context.reservation.first_name || '',
            last_name: context.reservation.last_name || '',
            email: context.reservation.email || '',
            admin_reservation_id: Number(context.reservation.admin_reservation_id),
            admin_unit_id: Number(context.unit.admin_unit_id),
        },
        survey_name: name,
        survey_type: type,
        prior_content: (context as any).prior_content,
        prior_tip: (context as any).prior_tip,
    });

    const renderStepInfo = (
        section: Section,
        context: {
            priorContent?: PriorPhoto[];
            priorTip?: string | null;
            adminReservationId: number;
            nsu: string;
            confirmation: string;
            unitName: string;
        }
    ): SurveyWizardStep => {
        const description =
            type === SurveyType.CONTENT
                ? `Show us your favorite spots at ${context.unitName}. Your photos help future guests feel at home.`
                : section.description;

        return {
            component: renderQuestionsContainer(section.section_questions, context),
            title: section.title,
            description,
            validationSchema: Yup.object().shape(getValidationSchema(section.section_questions)),
            rawQuestions: section.section_questions,
        };
    };

    const getValidationSchema = (questions: Array<Question>): any => {
        let validationSchema = new Map<string, any>();
        questions.forEach((question) => {
            const fieldDismissible = `${question.question_id}-${question.name}-${question.type}-${QuestionAttributeOptions.DISMISSED}`;
            let stringSchema = question.required ? Yup.string().required() : Yup.string().optional();
            const answerKey = `${question.question_id}-${question.name}-${question.type}-${QuestionAttributeOptions.ANSWER.toLowerCase()}`;

            if (question.type === QuestionType.PHOTO) {
                const photoOptions = (question.configuration as BaseOptions & PhotoOptions)?.options;
                const minPhotos = photoOptions?.min_photos ?? (question.required ? 1 : 0);
                validationSchema.set(
                    answerKey,
                    Yup.array()
                        .of(Yup.object({ s3_key: Yup.string().required() }))
                        .min(minPhotos, 'required')
                        .max(photoOptions?.max_photos ?? 10)
                        .default([])
                );
                return;
            }

            if (question.configuration.dismissible) {
                validationSchema.set(
                    fieldDismissible,
                    Yup.string().when(answerKey, {
                        is: undefined,
                        then: stringSchema,
                    })
                );
            }
            const isPublicReviewQuestion =
                _.isEqual(question.name, QuestionCategory.PUBLIC_REVIEW) && _.isEqual(question.type, QuestionType.COMMENT);
            if (commentRequired && isPublicReviewQuestion) {
                stringSchema = Yup.string().trim().required('required');
            }

            validationSchema.set(answerKey, question.configuration.dismissible ? Yup.string().optional() : stringSchema);
        });

        return Array.from(validationSchema.entries()).reduce((main, [key, value]) => ({ ...main, [key]: value }), {});
    };

    const getStepsInfo = (
        sections: Array<Section>,
        context: {
            priorContent?: PriorPhoto[];
            priorTip?: string | null;
            adminReservationId: number;
            nsu: string;
            confirmation: string;
            unitName: string;
        }
    ): Array<SurveyWizardStep> => {
        if (!sections) {
            return [];
        }
        return sections.map((step) => renderStepInfo(step, context));
    };

    const resolveSource = (resolvedSurveyName: string): SurveySources => {
        if (type === SurveyType.CONTENT) {
            return SurveySources.PRE_REVIEW_PAL_GUEST;
        }
        if (
            resolvedSurveyName === SurveyName.REVIEW_PAL_POSITIVE_DISPOSITION_WITH_CONTENT ||
            resolvedSurveyName === SurveyName.REVIEW_PAL_NEGATIVE_DISPOSITION_WITH_CONTENT
        ) {
            return SurveySources.PAL_GUEST_WITH_CONTENT;
        }
        return isPal ? SurveySources.PAL_GUEST : SurveySources.GUEST;
    };

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    });

    if (isLoading) {
        return (
            <div className="loading">
                <Spinner />
            </div>
        );
    }

    if (error) {
        const kind = (error as DetailedError)?.code || ErrorTypes.UNHANDLED;
        return <MessagePanel kind={kind as ErrorTypes} action={ActionSegment.PAGE_LOAD} />;
    }

    if (!data || _.isEmpty(data.data)) {
        return null;
    }

    const { attributes } = data.data;
    const generalInformation = getGeneralInformation(attributes);
    const questionContext = {
        priorContent: (attributes.context as any)?.prior_content as PriorPhoto[] | undefined,
        priorTip: (attributes.context as any)?.prior_tip as string | null | undefined,
        adminReservationId: Number(attributes.context.reservation.admin_reservation_id),
        nsu: nsuHash,
        confirmation: confirmationCode,
        unitName: attributes.context.unit.name,
    };
    const steps = getStepsInfo(attributes.sections, questionContext);
    const source = resolveSource(attributes.name);

    return <SurveyWizard surveyInfo={generalInformation} steps={steps} isPal={isPal} surveyType={type} surveySource={source} />;
};
